/**
 * Ted #35 (his Item 33; #1190 story 35.3, #948 story 1) — the words for a
 * client's agreed price, shared by the profile card and the settle dialog so
 * "Daycare · 2 dogs · Tue & Thu · $70.00" reads the same in both places
 * (Design Philosophy 1: one glance, one truth — the same fact, one wording).
 *
 * Pure: no React, no fetch. The server sends codes (`whyNot`); the sentences
 * live here, where the person at the counter reads them.
 */
import type { AdminAgreementWhyNot, AdminRateAgreement } from '../../lib/api.js';

export const WEEKDAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;
export const WEEKDAY_LONG = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'] as const;

export function formatMoney(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

/** "Tue & Thu", "Mon, Wed & Fri", "any day" for an empty list. */
export function formatWeekdays(weekdays: ReadonlyArray<number>): string {
  const names = [...new Set(weekdays)]
    .filter((d) => Number.isInteger(d) && d >= 0 && d <= 6)
    .sort((a, b) => a - b)
    .map((d) => WEEKDAY_SHORT[d]!);
  if (names.length === 0) return 'any day';
  if (names.length === 1) return names[0]!;
  return `${names.slice(0, -1).join(', ')} & ${names[names.length - 1]}`;
}

/** "2 dogs", "1 dog", "any number of dogs". */
export function dogCountLabel(dogCount: number | null): string {
  if (dogCount === null) return 'any number of dogs';
  return `${dogCount} ${dogCount === 1 ? 'dog' : 'dogs'}`;
}

/** What the price is FOR: "per dog" or "for the household that day". */
export function basisLabel(basis: AdminRateAgreement['basis']): string {
  return basis === 'per_dog' ? 'per dog' : 'for the household that day';
}

/** The row on the card: "Daycare · 2 dogs · Tue & Thu · $70.00 (standard $85.00)". */
export function agreementRowLabel(a: AdminRateAgreement): string {
  const service = a.serviceTypeDisplayName ?? a.serviceTypeName ?? 'Service';
  const dogs = a.dogCount === null ? (a.basis === 'per_dog' ? 'per dog' : 'household') : dogCountLabel(a.dogCount);
  const days = a.weekdays.length === 0 ? 'any day' : formatWeekdays(a.weekdays);
  const standard = a.standardPriceCents !== null ? ` (standard ${formatMoney(a.standardPriceCents)})` : '';
  return `${service} · ${dogs} · ${days} · ${formatMoney(a.priceCents)}${standard}`;
}

/** The settle dialog's offer: "Agreed rate on file: $70.00 for 2 dogs (Tue & Thu)". */
export function agreementOfferLabel(a: AdminRateAgreement): string {
  const who = a.dogCount !== null ? `for ${dogCountLabel(a.dogCount)}` : a.basis === 'per_dog' ? 'per dog' : 'for the household';
  const days = a.weekdays.length > 0 ? ` (${formatWeekdays(a.weekdays)})` : '';
  return `Agreed rate on file: ${formatMoney(a.priceCents)} ${who}${days}`;
}

/**
 * Why the deal does not apply today, in the desk's words. `serviceDate` is
 * the invoice's visit date (ISO) so "today is Wed" names the VISIT's day,
 * not the clock's — the two differ at end-of-day settles.
 */
export function agreementWhyNotCopy(
  a: AdminRateAgreement,
  whyNot: AdminAgreementWhyNot,
  serviceDate: string | null,
): string {
  const days = formatWeekdays(a.weekdays);
  switch (whyNot) {
    case 'wrong_weekday': {
      const d = serviceDate ? new Date(serviceDate) : null;
      const today = d && !Number.isNaN(d.getTime()) ? WEEKDAY_SHORT[d.getUTCDay()] : null;
      return `Agreed rate on file for ${days}${today ? ` — today is ${today}` : ' — not today'}.`;
    }
    case 'dog_count':
      return `Agreed rate on file for ${dogCountLabel(a.dogCount)} — a different number of dogs is checking out today.`;
    case 'already_applied':
      return 'Agreed rate already applied to this checkout.';
    case 'wrong_service':
      return `Agreed rate on file for ${a.serviceTypeDisplayName ?? a.serviceTypeName ?? 'another service'} — not this service.`;
    case 'no_date':
      return 'Agreed rate on file — this visit has no date to judge it by.';
    case 'inactive':
      return 'Agreed rate was removed from the profile.';
  }
}
